// components/privacyPolicy.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Home } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
              <Shield className="h-8 w-8 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-lg text-gray-600">
              Last updated: July 4, 2025
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Funding and Sustainability</h2>
              <p className="text-gray-700 mb-4">
                CPR Refresh is committed to remaining a free educational service. To cover hosting, development, and operational costs, we may:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Partner with healthcare education providers, medical device companies, or other relevant organizations</li>
                <li>Send occasional marketing communications about products, services, or educational opportunities from our partners</li>
                <li>Display sponsored content or advertisements that are relevant to CPR and emergency medical education</li>
                <li>Participate in affiliate programs for CPR training courses or emergency medical equipment</li>
              </ul>
              <p className="text-gray-700 mb-4">
                We are selective about our partnerships and only work with organizations that align with our mission of promoting life-saving education. All sponsored communications will be clearly identified as such.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 mb-4">
                CPR Refresh ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our CPR knowledge refresh service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
              <h3 className="text-xl font-medium text-gray-800 mb-3">Personal Information</h3>
              <p className="text-gray-700 mb-4">
                We collect the following personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Email address (for monthly quiz reminders)</li>
                <li>Quiz responses and scores (for educational purposes)</li>
                <li>Usage data (page views, time spent on quiz)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">
                We use your information to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Send monthly CPR quiz reminders via email</li>
                <li>Track your quiz progress and provide personalized feedback</li>
                <li>Improve our service and quiz content</li>
                <li>Communicate important updates about our service</li>
                <li>Send occasional marketing communications about relevant healthcare education opportunities, partnerships, or sponsors that help us maintain this free service</li>
                <li>Ensure the security and functionality of our platform</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Marketing Communications:</strong> We may occasionally send you information about educational partnerships, relevant healthcare products, or sponsor opportunities that help us cover hosting and operational costs to keep this service free. You can opt out of marketing emails at any time while maintaining your quiz reminders.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information Sharing</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties, except:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>With your explicit consent</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and safety</li>
                <li>With trusted service providers who assist in operating our service (under strict confidentiality agreements)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
              <p className="text-gray-700 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Unsubscribe from our email communications at any time</li>
                <li>Opt out of marketing communications while maintaining quiz reminder subscriptions</li>
                <li>Withdraw consent for data processing</li>
                <li>Object to sharing your information with sponsors or partners</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Granular Email Preferences:</strong> You can choose to receive only quiz reminders, only marketing communications, both, or neither. We respect your preferences and will honor your choices promptly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and Tracking</h2>
              <p className="text-gray-700 mb-4">
                We may use cookies and similar tracking technologies to enhance your experience on our website. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your personal information only as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page with an updated date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Contact:</strong>{' '}
                  <button
                    onClick={() => navigate('/contact')}
                    className="text-indigo-600 hover:text-indigo-700 underline"
                  >
                    Use our contact form
                  </button>
                </p>
              </div>
            </section>
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center px-6 py-3 border border-indigo-600 text-base font-medium rounded-lg text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Home className="mr-2 h-4 w-4" />
              Back to Home
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;